import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import { toast } from "sonner";
import { ArrowLeft, Download, Printer, QrCode as QrIcon, ScanLine, PenLine, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

export default function QRPage() {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState("");
  const [petition, setPetition] = useState(null);
  // Fixed public link of the published petition (change here when the custom domain is live).
  const petitionUrl = "https://qr-signatures.emergent.host/";

  useEffect(() => {
    api.get("/petition").then((r) => setPetition(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (canvasRef.current) {
      QRCode.toCanvas(canvasRef.current, petitionUrl, {
        width: 300,
        margin: 1,
        color: { dark: "#0F172A", light: "#FFFFFF" },
        errorCorrectionLevel: "H",
      });
    }
    QRCode.toDataURL(petitionUrl, {
      width: 1024,
      margin: 2,
      color: { dark: "#0F172A", light: "#FFFFFF" },
      errorCorrectionLevel: "H",
    }).then(setDataUrl);
  }, [petitionUrl, petition]);

  const downloadPng = () => {
    if (!dataUrl) {
      toast.error("QR non ancora pronto, attendi un istante");
      return;
    }
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = "qr-petizione.png";
    a.click();
    toast.success("QR Code scaricato");
  };

  const printPoster = () => window.print();

  const steps = [
    { icon: ScanLine, text: "Inquadra il QR code con la fotocamera del telefono" },
    { icon: PenLine, text: "Leggi la petizione e compila i tuoi dati" },
    { icon: ShieldCheck, text: "Conferma: la tua firma \u00e8 registrata in pochi secondi" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/80 backdrop-blur-xl no-print">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-1.5" data-testid="back-to-petition-button">
              <ArrowLeft className="w-4 h-4" /> Torna alla petizione
            </Button>
          </Link>
          <span className="font-heading font-bold flex items-center gap-2">
            <QrIcon className="w-5 h-5 text-primary" /> QR Code & Volantino
          </span>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid lg:grid-cols-5 gap-8 items-start">
        {/* Controls */}
        <div className="lg:col-span-2 no-print">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Volantino & QR Code</h1>
          <p className="text-muted-foreground mt-2">
            Scarica il QR o stampa il volantino A4 pronto da appendere in bar, negozi, bacheche e vetrine:
            chiunque lo scansiona può leggere e firmare la petizione dal proprio telefono.
          </p>
          <div className="mt-4 rounded-xl bg-secondary px-4 py-3 text-sm break-all font-mono">
            {petitionUrl}
          </div>
          <div className="flex flex-wrap gap-3 mt-6">
            <Button onClick={printPoster} className="rounded-xl gap-2" data-testid="print-poster-button">
              <Printer className="w-4 h-4" /> Stampa volantino A4
            </Button>
            <Button onClick={downloadPng} variant="outline" className="rounded-xl gap-2" data-testid="download-qr-button">
              <Download className="w-4 h-4" /> Scarica solo QR (PNG)
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Suggerimento: quando pubblichi l'app online, torna qui e rigenera il volantino così il QR
            punterà all'indirizzo definitivo.
          </p>
        </div>

        {/* A4 Poster */}
        <div className="lg:col-span-3 flex justify-center">
          <div id="poster" data-testid="qr-poster" className="a4-poster">
            <div className="poster-inner">
              <div className="poster-badge">
                <ShieldCheck className="w-4 h-4" /> PETIZIONE DEI CITTADINI
              </div>

              <h2 className="poster-title font-heading">
                {petition?.title || "Firma la petizione"}
              </h2>
              {petition?.subtitle && <p className="poster-subtitle">{petition.subtitle}</p>}

              <div className="poster-cta font-heading">SCANSIONA E FIRMA</div>

              <div className="poster-qr">
                <canvas ref={canvasRef} data-testid="qr-canvas" />
              </div>

              <div className="poster-steps">
                {steps.map((s, i) => (
                  <div className="poster-step" key={i}>
                    <div className="poster-step-num">{i + 1}</div>
                    <s.icon className="poster-step-icon" />
                    <span>{s.text}</span>
                  </div>
                ))}
              </div>

              <div className="poster-footer">
                <span className="font-heading">VoceCivica</span>
                <span>{petitionUrl.replace(/^https?:\/\//, "")}</span>
              </div>
            </div>
          </div>
        </div>
      </main>

      <style>{`
        .a4-poster {
          width: 100%;
          max-width: 480px;
          aspect-ratio: 210 / 297;
          background: linear-gradient(160deg, #ffffff 0%, #f7faf8 100%);
          border: 1px solid #E2E8F0;
          border-radius: 20px;
          box-shadow: 0 20px 50px -20px rgba(15,23,42,0.25);
          overflow: hidden;
        }
        .poster-inner {
          height: 100%;
          padding: 7% 8%;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }
        .poster-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 700;
          letter-spacing: 0.12em;
          color: #92400E;
          background: #FEF3C7;
          padding: 6px 14px;
          border-radius: 999px;
        }
        .poster-title {
          font-size: clamp(20px, 4.4vw, 30px);
          font-weight: 800;
          line-height: 1.1;
          color: #0F172A;
          margin: 18px 0 0;
        }
        .poster-subtitle {
          font-size: 13px;
          color: #475569;
          margin-top: 8px;
          line-height: 1.4;
        }
        .poster-cta {
          font-size: clamp(22px, 5vw, 34px);
          font-weight: 800;
          color: #059669;
          letter-spacing: 0.02em;
          margin: 22px 0 14px;
        }
        .poster-qr {
          background: #fff;
          padding: 14px;
          border-radius: 16px;
          border: 2px solid #ECFDF5;
          box-shadow: 0 8px 24px -12px rgba(5,150,105,0.4);
        }
        .poster-qr canvas { display: block; width: 100% !important; height: auto !important; max-width: 240px; }
        .poster-steps {
          margin-top: 22px;
          width: 100%;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .poster-step {
          display: flex;
          align-items: center;
          gap: 10px;
          text-align: left;
          font-size: 13px;
          color: #1E293B;
          background: #F8FAFC;
          border: 1px solid #EEF2F6;
          border-radius: 12px;
          padding: 10px 12px;
        }
        .poster-step-num {
          flex: 0 0 auto;
          width: 24px; height: 24px;
          border-radius: 999px;
          background: #059669; color: #fff;
          font-weight: 700; font-size: 13px;
          display: flex; align-items: center; justify-content: center;
        }
        .poster-step-icon { width: 18px; height: 18px; color: #059669; flex: 0 0 auto; }
        .poster-footer {
          margin-top: auto;
          padding-top: 18px;
          width: 100%;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          color: #64748B;
          border-top: 1px dashed #E2E8F0;
        }
        .poster-footer .font-heading { color: #059669; font-weight: 800; }

        @media print {
          @page { size: A4; margin: 0; }
          .no-print { display: none !important; }
          body { background: #fff; }
          main { display: block !important; padding: 0 !important; margin: 0 !important; max-width: none !important; }
          #poster, .a4-poster {
            width: 210mm !important;
            height: 297mm !important;
            max-width: none !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            aspect-ratio: auto !important;
          }
          .poster-qr canvas { max-width: 260px; }
        }
      `}</style>
    </div>
  );
}
